/**
 * Third-party risk management.
 *
 * One screen covering the register, the risk picture and the detail of any one
 * supplier. Deliberately not split across three routes: the whole point of TPRM
 * is seeing a supplier's inherent risk, their assessment result and their scan
 * findings *together*, because each one alone is misleading.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, qs } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApiError } from '../lib/useApiError';
import {
  Card, CardHeader, Button, Pill, Modal, Field, EmptyState, Skeleton,
  ErrorNotice, StatTile, TONE_FOR_STATUS, type Tone,
} from '../components/ui';
import { RiskScatter, type RiskPoint } from '../components/charts';
import { IconVendor, IconShield } from '../components/icons';
import { RISK_LEVELS, bandFor, type RiskLevel } from '@opencomply/shared';
import { cn } from '../lib/cn';

interface Vendor {
  id: string; name: string; domain: string; contactEmail: string | null;
  category: string | null; inherentRisk: string; isActive: boolean;
  complianceRisk: number | null; securityRisk: number | null;
  compositeRisk: number | null; risksComputedAt: string | null;
  _count?: { assessments: number; securityScans: number };
  securityScans?: Array<{ id: string; completedAt: string | null; redCount: number; amberCount: number; greenCount: number }>;
  assessments?: Array<{ id: string; status: string; complianceScore: number | null; dueDate: string }>;
}

interface Finding {
  key: string; band: 'RED' | 'AMBER' | 'GREEN';
  title: string; detail: string; remediation?: string; category: string;
}

/** Band → tone. Risk bands are statuses, so they use the reserved ramp. */
const BAND_TONE: Record<string, Tone> = {
  LOW: 'good', MEDIUM: 'warning', HIGH: 'serious', CRITICAL: 'critical',
};

export function VendorsPage() {
  const { t, i18n } = useTranslation();
  const { allows } = useAuth();
  const queryClient = useQueryClient();
  const describeError = useApiError();

  const [search, setSearch] = useState('');
  const [riskFilter, setRiskFilter] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, error: listError, refetch } = useQuery({
    queryKey: ['vendors', { search, riskFilter }],
    queryFn: () =>
      api.get<{ items: Vendor[]; total: number }>(
        `/vendors${qs({ search, riskLevel: riskFilter, pageSize: 100 })}`,
      ),
  });

  const { data: chart } = useQuery({
    queryKey: ['vendors', 'risk-chart'],
    queryFn: () =>
      api.get<{
        vendors: Array<Vendor & { inherentScore: number; measured: number }>;
      }>('/vendors/risk-chart'),
  });

  const items = data?.items ?? [];

  const formatDate = (iso: string | null) =>
    iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(iso)) : '—';

  const points: RiskPoint[] = (chart?.vendors ?? [])
    .filter((v) => v.compositeRisk !== null)
    .map((v) => ({
      id: v.id,
      name: v.name,
      x: v.inherentScore,
      y: v.compositeRisk!,
      band: bandFor(v.compositeRisk!) as RiskPoint['band'],
      measured: v.measured,
    }));

  const kpis = {
    total: items.length,
    critical: items.filter((v) => v.compositeRisk !== null && bandFor(v.compositeRisk) === 'CRITICAL').length,
    unscanned: items.filter((v) => (v._count?.securityScans ?? 0) === 0).length,
    unassessed: items.filter((v) => (v._count?.assessments ?? 0) === 0).length,
  };

  return (
    <div className="oc-page space-y-5 animate-fade-in">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="oc-h1">{t('vendors.title')}</h1>
          <p className="oc-muted mt-1">{t('vendors.subtitle')}</p>
        </div>
        {allows('vendor.create') && (
          <div className="flex gap-2">
            <Button size="sm" onClick={() => { setError(null); setImportOpen(true); }}>
              {t('vendors.addMultiple')}
            </Button>
            <Button size="sm" variant="primary" onClick={() => { setError(null); setAddOpen(true); }}>
              {t('vendors.add')}
            </Button>
          </div>
        )}
      </header>

      {error && <ErrorNotice message={error} />}

      {items.length > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <StatTile label={t('common.total')} value={kpis.total} />
            <StatTile label={t('status.CRITICAL')} value={kpis.critical}
                      tone={kpis.critical > 0 ? 'critical' : undefined} />
            <StatTile label={t('vendors.notScanned')} value={kpis.unscanned}
                      tone={kpis.unscanned > 0 ? 'serious' : undefined} />
            <StatTile label={t('vendors.unassessed')} value={kpis.unassessed} />
          </div>

          <Card>
            <CardHeader title={t('vendors.riskChart')} subtitle={t('vendors.riskChartSubtitle')} />
            <RiskScatter points={points} onSelect={(id) => setDetailId(id)} />
            <p className="mt-3 text-xs text-ink-muted leading-relaxed">
              {t('vendors.riskChartHint')}
            </p>
          </Card>
        </>
      )}

      <Card className="!p-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            className="oc-input sm:max-w-xs" type="search"
            placeholder={t('vendors.searchPlaceholder')}
            value={search} onChange={(e) => setSearch(e.target.value)}
          />
          <select className="oc-input sm:!w-auto" value={riskFilter}
                  onChange={(e) => setRiskFilter(e.target.value)}
                  aria-label={t('vendors.inherentRisk')}>
            <option value="">{t('common.all')}</option>
            {RISK_LEVELS.map((r) => <option key={r} value={r}>{t(`status.${r}`)}</option>)}
          </select>
        </div>
      </Card>

      {isLoading ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20" />)}</div>
      ) : listError ? (
        <ErrorNotice message={describeError(listError)} onRetry={() => void refetch()}
                     retryLabel={t('common.retry')} />
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<IconVendor className="w-10 h-10" />}
            title={t('vendors.none')}
            action={allows('vendor.create')
              ? <Button variant="primary" onClick={() => setAddOpen(true)}>{t('vendors.add')}</Button>
              : undefined}
          />
        </Card>
      ) : (
        <ul className="grid gap-3 md:grid-cols-2">
          {items.map((v) => {
            const band = v.compositeRisk !== null ? bandFor(v.compositeRisk) : null;
            const latestScan = v.securityScans?.[0];
            return (
              <li key={v.id}>
                <button
                  onClick={() => setDetailId(v.id)}
                  className="oc-card p-4 w-full text-left hover:shadow-raised transition-shadow"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <p className="font-medium text-ink truncate">{v.name}</p>
                      <p className="text-xs text-ink-muted font-mono truncate">{v.domain}</p>
                    </div>
                    {band ? (
                      <Pill tone={BAND_TONE[band] ?? 'neutral'} className="shrink-0">
                        {t(`status.${band}`)} · {v.compositeRisk}
                      </Pill>
                    ) : (
                      <Pill tone="neutral" className="shrink-0">{t('vendors.notMeasured')}</Pill>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    <Pill tone={TONE_FOR_STATUS[v.inherentRisk] ?? 'neutral'}>
                      {t('vendors.inherent')}: {t(`status.${v.inherentRisk}`)}
                    </Pill>
                    {v.category && <span className="oc-pill-neutral">{v.category}</span>}
                    {!v.isActive && <Pill tone="neutral">{t('vendors.inactive')}</Pill>}
                  </div>

                  {latestScan ? (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-critical font-medium tabular-nums">{latestScan.redCount} ✗</span>
                      <span className="text-warning font-medium tabular-nums">{latestScan.amberCount} !</span>
                      <span className="text-good font-medium tabular-nums">{latestScan.greenCount} ✓</span>
                      <span className="text-ink-muted">
                        · {t('vendors.lastScan', { date: formatDate(latestScan.completedAt) })}
                      </span>
                    </div>
                  ) : (
                    <p className="text-xs text-ink-muted">{t('vendors.noScans')}</p>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <AddVendorModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={() => { setAddOpen(false); void queryClient.invalidateQueries({ queryKey: ['vendors'] }); }}
      />
      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => { setImportOpen(false); void queryClient.invalidateQueries({ queryKey: ['vendors'] }); }}
      />
      <VendorDetail vendorId={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}

// ---------------------------------------------------------------------------

function AddVendorModal({ open, onClose, onSaved }: {
  open: boolean; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const describeError = useApiError();
  const [form, setForm] = useState({
    name: '', domain: '', contactEmail: '', category: '', inherentRisk: 'MEDIUM' as RiskLevel,
  });
  const [error, setError] = useState<string | null>(null);
  const [suggestedRoot, setSuggestedRoot] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.post('/vendors', {
        name: form.name,
        domain: form.domain,
        contactEmail: form.contactEmail || null,
        category: form.category || null,
        inherentRisk: form.inherentRisk,
      }),
    onSuccess: () => {
      setForm({ name: '', domain: '', contactEmail: '', category: '', inherentRisk: 'MEDIUM' });
      setError(null);
      setSuggestedRoot(null);
      onSaved();
    },
    onError: (err) => {
      setError(describeError(err));
      // The server tells us the root it wanted; offer it as a one-click fix
      // rather than making the user work out what was wrong.
      const detail = (err as { details?: { suggestedRoot?: string } }).details;
      setSuggestedRoot(detail?.suggestedRoot ?? null);
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('vendors.add')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={!form.name.trim() || !form.domain.trim() || create.isPending}
                  onClick={() => create.mutate()}>
            {create.isPending ? t('common.saving') : t('common.create')}
          </Button>
        </>
      }
    >
      {error && (
        <div className="mb-4">
          <ErrorNotice message={error} />
          {suggestedRoot && (
            <button
              onClick={() => { setForm({ ...form, domain: suggestedRoot }); setError(null); setSuggestedRoot(null); }}
              className="mt-2 text-sm text-brand hover:underline"
            >
              Use {suggestedRoot} instead
            </button>
          )}
        </div>
      )}

      <Field label={t('vendors.name')} htmlFor="v-name" required>
        <input id="v-name" className="oc-input" value={form.name} maxLength={255}
               onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </Field>
      <Field label={t('vendors.domain')} htmlFor="v-domain" hint={t('vendors.domainHint')} required>
        <input id="v-domain" className="oc-input font-mono" value={form.domain}
               placeholder="example.com" autoCapitalize="none" autoCorrect="off"
               onChange={(e) => setForm({ ...form, domain: e.target.value })} />
      </Field>
      <Field label={t('vendors.contactEmail')} htmlFor="v-email" hint={t('vendors.emailHint')}>
        <input id="v-email" type="email" className="oc-input" value={form.contactEmail}
               autoCapitalize="none" autoCorrect="off"
               onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} />
      </Field>
      <Field label={t('vendors.category')} htmlFor="v-cat" hint={t('common.optional')}>
        <input id="v-cat" className="oc-input" value={form.category} maxLength={100}
               onChange={(e) => setForm({ ...form, category: e.target.value })} />
      </Field>
      <Field label={t('vendors.inherentRisk')} htmlFor="v-risk">
        <select id="v-risk" className="oc-input" value={form.inherentRisk}
                onChange={(e) => setForm({ ...form, inherentRisk: e.target.value as RiskLevel })}>
          {RISK_LEVELS.map((r) => <option key={r} value={r}>{t(`status.${r}`)}</option>)}
        </select>
      </Field>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

/**
 * Bulk import.
 *
 * Preview then commit, never a single blind upload: a 200-row file that
 * half-imports and reports "183 succeeded" leaves the user to work out which
 * seventeen failed and why. Every row's verdict is shown first.
 */
function ImportModal({ open, onClose, onImported }: {
  open: boolean; onClose: () => void; onImported: () => void;
}) {
  const { t } = useTranslation();
  const describeError = useApiError();
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const preview = useMutation({
    mutationFn: (text: string) =>
      api.post<{
        rows: Array<{
          line: number; name: string; domain: string; valid: boolean;
          error?: string; errorDetail?: Record<string, string>;
        }>;
        validCount: number;
      }>('/vendors/import/preview', { csv: text }),
    onError: (err) => setError(describeError(err)),
  });

  const commit = useMutation({
    mutationFn: () => api.post<{ imported: number; skipped: number }>('/vendors/import/commit', { csv }),
    // Do NOT close on success: show the "X added, Y skipped" summary so the result
    // of a bulk action is never left to guesswork. The parent refreshes the list
    // when the dialog is finally closed.
    onError: (err) => setError(describeError(err)),
  });

  const done = () => {
    setCsv(''); setFileName(''); preview.reset(); commit.reset(); onImported();
  };

  const onFile = async (file: File) => {
    setError(null);
    const text = await file.text();
    setCsv(text);
    setFileName(file.name);
    preview.mutate(text);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('vendors.import.title')}
      footer={
        commit.isSuccess ? (
          <Button variant="primary" onClick={done}>{t('common.done', 'Done')}</Button>
        ) : (
          <>
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={!preview.data || preview.data.validCount === 0 || commit.isPending}
              onClick={() => commit.mutate()}
            >
              {commit.isPending
                ? t('common.saving')
                : t('vendors.import.commit', { count: preview.data?.validCount ?? 0 })}
            </Button>
          </>
        )
      }
    >
      {error && <div className="mb-4"><ErrorNotice message={error} /></div>}

      {commit.isSuccess && commit.data && (
        <div className="mb-4 rounded-xl bg-good/[0.08] border border-good/30 p-3">
          <p className="text-sm font-medium text-ink">
            {t('vendors.import.result', {
              defaultValue: '{{imported}} added, {{skipped}} skipped.',
              imported: commit.data.imported,
              skipped: commit.data.skipped,
            })}
          </p>
          {commit.data.skipped > 0 && (
            <p className="text-xs text-ink-secondary mt-1">
              {t('vendors.import.resultHint', 'Skipped rows and their reasons are listed below.')}
            </p>
          )}
        </div>
      )}

      <p className="oc-muted mb-3">{t('vendors.import.description')}</p>

      <a
        href="/api/vendors/import/template"
        className="inline-block mb-4 text-sm text-brand hover:underline"
      >
        {t('vendors.import.downloadTemplate')}
      </a>

      <label className="block mb-4">
        <span className="oc-label">{t('vendors.import.selectFile')}</span>
        <input
          type="file"
          accept=".csv,text/csv"
          className="block w-full text-sm text-ink-secondary
                     file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0
                     file:text-sm file:font-medium file:bg-brand/10 file:text-brand
                     hover:file:bg-brand/20"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void onFile(file);
          }}
        />
        <span className="block mt-1 text-xs text-ink-muted">{t('vendors.import.csvOnly')}</span>
      </label>

      {fileName && <p className="text-xs text-ink-muted mb-2 font-mono">{fileName}</p>}

      {preview.data && (
        <div>
          <p className={cn(
            'text-sm font-medium mb-2',
            preview.data.validCount === 0 ? 'text-critical' : 'text-ink',
          )}>
            {t('vendors.import.validRows', {
              valid: preview.data.validCount,
              total: preview.data.rows.length,
            })}
          </p>
          <ul className="space-y-1 max-h-60 overflow-y-auto">
            {preview.data.rows.map((row) => (
              <li
                key={row.line}
                className={cn(
                  'text-xs rounded-lg px-2.5 py-1.5 flex items-start gap-2',
                  row.valid ? 'bg-good/[0.08]' : 'bg-critical/[0.08]',
                )}
              >
                <span className="text-ink-muted shrink-0 tabular-nums">
                  {t('vendors.import.line')} {row.line}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="text-ink">{row.name || '—'}</span>
                  {row.error && (
                    <span className="block text-critical mt-0.5">
                      {t(`error.${row.error}`, {
                        defaultValue: row.error,
                        ...(row.errorDetail ?? {}),
                      })}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------

/**
 * Vendor detail.
 *
 * Shows the three risk inputs side by side rather than only the composite,
 * because a composite of 1.6 means something completely different when it comes
 * from one measured input than from two. The "how this is calculated" panel is
 * expanded by default the first time for the same reason — a risk score nobody
 * can explain is a risk score nobody should act on.
 */
function VendorDetail({ vendorId, onClose }: { vendorId: string | null; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const { allows } = useAuth();
  const queryClient = useQueryClient();
  const describeError = useApiError();

  const [scanOpen, setScanOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: vendor, isLoading } = useQuery({
    queryKey: ['vendor', vendorId],
    queryFn: () =>
      // Omit the list-shaped fields before intersecting: the Vendor interface
      // declares narrower versions for the list view, and an intersection would
      // keep those rather than the richer ones the detail endpoint returns.
      api.get<Omit<Vendor, 'securityScans' | 'assessments'> & {
        latestFindings: Finding[];
        securityScans: Array<{
          id: string; status: string; domain: string; scheduledFor: string;
          completedAt: string | null; redCount: number; amberCount: number;
          greenCount: number; errorMessage: string | null;
        }>;
        notes: Array<{ id: string; body: string; authorName: string | null; createdAt: string }>;
        assessments: Array<{ id: string; status: string; complianceScore: number | null; dueDate: string }>;
      }>(`/vendors/${vendorId}`),
    enabled: !!vendorId,
  });

  const runScan = useMutation({
    mutationFn: (scanId: string) => api.post(`/scans/${scanId}/run`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['vendor', vendorId] });
      void queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
    onError: (err) => setError(describeError(err)),
  });

  const addNote = useMutation({
    mutationFn: () => api.post(`/vendors/${vendorId}/notes`, { body: noteText }),
    onSuccess: () => {
      setNoteText('');
      void queryClient.invalidateQueries({ queryKey: ['vendor', vendorId] });
    },
    onError: (err) => setError(describeError(err)),
  });

  const formatDate = (iso: string | null) =>
    iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso)) : '—';

  if (!vendorId) return null;

  const band = vendor?.compositeRisk !== null && vendor?.compositeRisk !== undefined
    ? bandFor(vendor.compositeRisk) : null;
  const pendingScan = vendor?.securityScans.find((s) => s.status === 'PENDING');
  const measured = vendor
    ? [vendor.complianceRisk !== null, vendor.securityRisk !== null].filter(Boolean).length
    : 0;

  return (
    <>
      <Modal open={!!vendorId} onClose={onClose} title={vendor?.name ?? t('common.loading')}>
        {isLoading || !vendor ? (
          <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20" />)}</div>
        ) : (
          <div className="space-y-5">
            {error && <ErrorNotice message={error} />}

            <div>
              <p className="font-mono text-sm text-ink-secondary">{vendor.domain}</p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {vendor.category && <span className="oc-pill-neutral">{vendor.category}</span>}
                <Pill tone={vendor.isActive ? 'good' : 'neutral'}>
                  {vendor.isActive ? t('vendors.active') : t('vendors.inactive')}
                </Pill>
                {vendor.contactEmail && (
                  <span className="oc-pill-neutral truncate max-w-[16rem]">{vendor.contactEmail}</span>
                )}
              </div>
            </div>

            {/* --- Risk ------------------------------------------------- */}
            <section>
              <div className="flex items-baseline justify-between gap-3 mb-3">
                <h3 className="oc-h2 text-base">{t('vendors.riskTitle')}</h3>
                {band && (
                  <Pill tone={BAND_TONE[band] ?? 'neutral'}>
                    {t(`status.${band}`)} · {vendor.compositeRisk}
                  </Pill>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2 mb-2">
                {([
                  ['vendors.inherent', vendor.inherentRisk, null],
                  ['vendors.compliance', null, vendor.complianceRisk],
                  ['vendors.security', null, vendor.securityRisk],
                ] as const).map(([label, level, score]) => (
                  <div key={label} className="rounded-xl bg-surface-sunken p-3 text-center">
                    <p className="text-[11px] uppercase tracking-wide text-ink-muted mb-1">
                      {t(label)}
                    </p>
                    <p className={cn(
                      'text-lg font-semibold tabular-nums',
                      score === null && level === null ? 'text-ink-muted' : 'text-ink',
                    )}>
                      {level ? t(`status.${level}`) : score !== null ? score : '—'}
                    </p>
                    {score === null && level === null && (
                      <p className="text-[10px] text-ink-muted mt-0.5">{t('vendors.notMeasured')}</p>
                    )}
                  </div>
                ))}
              </div>

              <p className="text-xs text-ink-muted">
                {t('vendors.measuredOf', { count: measured })}
              </p>

              <details className="mt-2">
                <summary className="text-xs text-brand cursor-pointer hover:underline">
                  {t('vendors.howCalculated')}
                </summary>
                <p className="mt-2 text-xs text-ink-secondary leading-relaxed border-l-2 border-brand/30 pl-3">
                  {t('vendors.riskExplainer')}
                </p>
              </details>
            </section>

            {/* --- Security scan ---------------------------------------- */}
            <section>
              <div className="flex items-center justify-between gap-3 mb-3">
                <h3 className="oc-h2 text-base">{t('vendors.scanTitle')}</h3>
                {allows('scan.schedule') && (
                  pendingScan ? (
                    <Button size="sm" variant="primary" disabled={runScan.isPending}
                            onClick={() => runScan.mutate(pendingScan.id)}>
                      {runScan.isPending ? t('vendors.scanRunning') : t('vendors.scanNow')}
                    </Button>
                  ) : (
                    <Button size="sm" onClick={() => { setError(null); setScanOpen(true); }}>
                      {t('vendors.scanSchedule')}
                    </Button>
                  )
                )}
              </div>

              {vendor.securityScans.length === 0 ? (
                <p className="text-sm text-ink-muted">{t('vendors.noScans')}</p>
              ) : (
                <>
                  <ul className="space-y-1.5 mb-3">
                    {vendor.securityScans.slice(0, 4).map((s) => (
                      <li key={s.id} className="flex items-center justify-between gap-3 text-xs
                                                 rounded-lg bg-surface-sunken px-3 py-2">
                        <span className="text-ink-secondary">
                          {s.status === 'READY' ? formatDate(s.completedAt) : formatDate(s.scheduledFor)}
                        </span>
                        {s.status === 'READY' ? (
                          <span className="flex items-center gap-2 tabular-nums">
                            <span className="text-critical">{s.redCount} ✗</span>
                            <span className="text-warning">{s.amberCount} !</span>
                            <span className="text-good">{s.greenCount} ✓</span>
                          </span>
                        ) : (
                          <Pill tone={s.status === 'FAILED' ? 'critical' : 'neutral'}>
                            {s.status === 'FAILED' ? t('vendors.scanFailed') : t(`status.${s.status}`, { defaultValue: s.status })}
                          </Pill>
                        )}
                      </li>
                    ))}
                  </ul>

                  {vendor.latestFindings.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-ink-secondary mb-2">
                        {t('vendors.findings')}
                      </p>
                      <ul className="space-y-1.5">
                        {/* Worst first: a report that opens on passing checks
                            buries the thing the reader needs. */}
                        {[...vendor.latestFindings]
                          .sort((a, b) =>
                            ({ RED: 0, AMBER: 1, GREEN: 2 })[a.band] -
                            ({ RED: 0, AMBER: 1, GREEN: 2 })[b.band])
                          .map((f) => (
                            <li key={f.key} className={cn(
                              'rounded-xl border-l-4 bg-surface-sunken p-3',
                              f.band === 'RED' && 'border-l-critical',
                              f.band === 'AMBER' && 'border-l-warning',
                              f.band === 'GREEN' && 'border-l-good',
                            )}>
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-sm font-medium text-ink">{f.title}</p>
                                <span className="oc-pill-neutral shrink-0">{f.category}</span>
                              </div>
                              <p className="text-xs text-ink-secondary mt-1 leading-relaxed">{f.detail}</p>
                              {f.remediation && (
                                <p className="text-xs text-brand mt-1.5 leading-relaxed">
                                  <span className="font-medium">{t('vendors.remediation')}: </span>
                                  {f.remediation}
                                </p>
                              )}
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </section>

            {/* --- Assessments ------------------------------------------ */}
            <section>
              <h3 className="oc-h2 text-base mb-3">{t('vendors.assessments')}</h3>
              {vendor.assessments.length === 0 ? (
                <p className="text-sm text-ink-muted">{t('vendors.noAssessments')}</p>
              ) : (
                <ul className="space-y-1.5">
                  {vendor.assessments.map((a) => (
                    <li key={a.id} className="flex items-center justify-between gap-3 text-xs
                                               rounded-lg bg-surface-sunken px-3 py-2">
                      <span className="text-ink-secondary">{formatDate(a.dueDate)}</span>
                      <span className="flex items-center gap-2">
                        {a.complianceScore !== null && (
                          <span className="tabular-nums text-ink">{a.complianceScore}%</span>
                        )}
                        <Pill tone={TONE_FOR_STATUS[a.status] ?? 'neutral'}>
                          {t(`status.${a.status}`, { defaultValue: a.status })}
                        </Pill>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* --- Notes ------------------------------------------------ */}
            <section>
              <h3 className="oc-h2 text-base mb-3">{t('vendors.notes')}</h3>
              {allows('vendor.update') && (
                <div className="mb-3">
                  <textarea
                    className="oc-input min-h-[4.5rem] py-2 resize-y"
                    placeholder={t('vendors.notePlaceholder')}
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    maxLength={5000}
                  />
                  <Button size="sm" className="mt-2" disabled={!noteText.trim() || addNote.isPending}
                          onClick={() => addNote.mutate()}>
                    {t('vendors.addNote')}
                  </Button>
                </div>
              )}
              {vendor.notes.length === 0 ? (
                <p className="text-sm text-ink-muted">—</p>
              ) : (
                <ul className="space-y-2">
                  {vendor.notes.map((n) => (
                    <li key={n.id} className="rounded-xl bg-surface-sunken p-3">
                      <p className="text-sm text-ink whitespace-pre-wrap">{n.body}</p>
                      <p className="text-[11px] text-ink-muted mt-1.5">
                        {n.authorName ?? '—'} · {formatDate(n.createdAt)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}
      </Modal>

      <ScheduleScanModal
        open={scanOpen}
        vendorId={vendorId}
        domain={vendor?.domain ?? ''}
        onClose={() => setScanOpen(false)}
        onScheduled={() => {
          setScanOpen(false);
          void queryClient.invalidateQueries({ queryKey: ['vendor', vendorId] });
          void queryClient.invalidateQueries({ queryKey: ['credits'] });
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------

/**
 * Scan scheduling.
 *
 * Credits are spent here, so the dialog states the exact cost, the resulting
 * balance and the number of runs before anything happens — and requires an
 * explicit acknowledgement, which the server also insists on.
 *
 * It also states plainly what the scan does and does not do. A user scheduling
 * a scan against a supplier's domain is taking an action against someone else's
 * infrastructure; they are entitled to know it is passive before they do it.
 */
function ScheduleScanModal({ open, vendorId, domain, onClose, onScheduled }: {
  open: boolean; vendorId: string | null; domain: string;
  onClose: () => void; onScheduled: () => void;
}) {
  const { t } = useTranslation();
  const describeError = useApiError();
  const [frequency, setFrequency] = useState<'SINGLE' | 'MONTHLY' | 'QUARTERLY'>('SINGLE');
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: pricing } = useQuery({
    queryKey: ['scans', 'pricing'],
    queryFn: () =>
      api.get<{
        balance: number; unitPrice: number;
        packages: Array<{
          frequency: string; runs: number; totalCost: number;
          balanceAfter: number; affordable: boolean;
        }>;
      }>('/scans/pricing'),
    enabled: open,
  });

  const schedule = useMutation({
    mutationFn: () =>
      api.post('/scans/schedule', { vendorId, domain, frequency, acknowledged: true }),
    onSuccess: () => { setAcknowledged(false); onScheduled(); },
    onError: (err) => setError(describeError(err)),
  });

  const selected = pricing?.packages.find((p) => p.frequency === frequency);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('vendors.scanSchedule')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            disabled={!acknowledged || !selected?.affordable || schedule.isPending}
            onClick={() => schedule.mutate()}
          >
            {schedule.isPending ? t('common.saving') : t('common.confirm')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <ErrorNotice message={error} />}

        <div className="rounded-xl bg-surface-sunken px-3 py-2.5 flex items-start gap-2">
          <IconShield className="w-4 h-4 text-brand shrink-0 mt-0.5" />
          <p className="text-xs text-ink-secondary leading-relaxed">{t('vendors.scanNotice')}</p>
        </div>

        <p className="font-mono text-sm text-ink">{domain}</p>

        <Field label={t('vendors.frequency')} htmlFor="scan-freq">
          <select id="scan-freq" className="oc-input" value={frequency}
                  onChange={(e) => { setFrequency(e.target.value as typeof frequency); setAcknowledged(false); }}>
            {(['SINGLE', 'QUARTERLY', 'MONTHLY'] as const).map((f) => {
              const pkg = pricing?.packages.find((p) => p.frequency === f);
              return (
                <option key={f} value={f}>
                  {t(`vendors.${f}`)}
                  {pkg ? ` — ${t('vendors.runs', { count: pkg.runs })}, ${pkg.totalCost}` : ''}
                </option>
              );
            })}
          </select>
        </Field>

        {selected && (
          <dl className="rounded-xl bg-surface-sunken p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-ink-secondary">{t('vendors.runs', { count: selected.runs })}</dt>
              <dd className="tabular-nums font-medium text-ink">{selected.runs}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-ink-secondary">{t('vendors.scanCost', { count: selected.totalCost })}</dt>
              <dd className="tabular-nums font-medium text-ink">{selected.totalCost}</dd>
            </div>
            <div className="flex justify-between pt-2 border-t border-line">
              <dt className="text-ink-secondary">
                {t('vendors.balanceAfter', { count: selected.balanceAfter })}
              </dt>
              <dd className={cn(
                'tabular-nums font-medium',
                selected.affordable ? 'text-ink' : 'text-critical',
              )}>
                {selected.balanceAfter}
              </dd>
            </div>
          </dl>
        )}

        {selected && (
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 w-4 h-4 accent-[rgb(var(--brand))]"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />
            <span className="text-sm text-ink">
              {t('vendors.scanAcknowledge', { count: selected.totalCost })}
            </span>
          </label>
        )}
      </div>
    </Modal>
  );
}
